import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { DownloadSimple } from '@phosphor-icons/react';

export default function UserReportsPage() {
  const { selectedBot, bots } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const bot = bots.find(b => b.id === selectedBot);

  useEffect(() => {
    if (!selectedBot) { setUsers([]); return; }
    setLoading(true);
    API.get(`/users/${selectedBot}`).then(r => setUsers(r.data.users || [])).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, [selectedBot]);

  const downloadCsv = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/users/${selectedBot}/csv`, '_blank');
  };

  if (!selectedBot) {
    return (
      <div className="space-y-4" data-testid="user-reports-page">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>User Reports</h1>
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">Select a bot from the sidebar</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>User Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">{users.length} users in {bot?.bot_name}</p>
        </div>
        <Button data-testid="download-users-csv" variant="outline" onClick={downloadCsv} disabled={users.length === 0} className="gap-2">
          <DownloadSimple size={18} /> Download CSV
        </Button>
      </div>

      {loading ? <div className="text-center py-12 text-muted-foreground">Loading...</div> : (
        <div className="border border-border rounded-md overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">User ID</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Username</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Joined</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Broadcasts</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} className={`border-b border-border ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                  <td className="px-4 py-3 font-mono text-xs">{u.telegram_user_id}</td>
                  <td className="px-4 py-3">{u.username || '-'}</td>
                  <td className="px-4 py-3">{u.first_name} {u.last_name || ''}</td>
                  <td className="px-4 py-3 font-mono text-xs">{u.joined_at?.replace('T', ' ').slice(0, 19)}</td>
                  <td className="px-4 py-3">
                    <Badge variant={u.is_active ? 'default' : 'destructive'} className="text-xs">{u.is_active ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{u.broadcast_count || 0}</td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No users yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
